import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Copy, KeyRound, X } from 'lucide-react';
import { fieldClass, ghostBtn, helperClass, labelClass, primaryBtn, SearchableSelect } from '../components/GatewayUi';
import { llmInferenceBaseUrl, llmInferenceCurlSample } from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import type { QuotaDirectoryUser, QuotaScope } from '../governedMcpTypes';
import { departmentMemberIds } from '../quotaUsage';

export type InferenceKeyTarget = {
  id: string;
  name: string;
  publicAlias: string;
  llmType?: string;
  routeKey: string;
  scope?: QuotaScope;
  scopeId?: string;
  scopeLabel?: string;
  hasInferenceKey?: boolean;
  inferenceKeyLast4?: string | null;
  source?: 'gateway' | 'quota';
};

type IssuedKey = {
  token: string;
  expiresAt: string;
  last4: string;
  baseUrl: string;
  model: string;
};

function coveredUsers(
  users: QuotaDirectoryUser[],
  memberIds: string[],
  scope: QuotaScope,
  scopeId: string
): QuotaDirectoryUser[] {
  if (scope === 'user') return users.filter((user) => user.id === scopeId);
  if (scope === 'role') return users.filter((user) => user.roleId === scopeId);
  return users.filter((user) => memberIds.includes(user.id));
}

export default function InferenceKeyDialog({
  target,
  isLight,
  onClose,
  onToast,
  onIssued,
}: {
  target: InferenceKeyTarget | null;
  isLight: boolean;
  onClose: () => void;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
  onIssued?: (next: { last4: string }) => void;
}) {
  const { issueInferenceKey, issueGatewayInferenceKey, quotaDirectory } = useGovernedMcp();
  const [userId, setUserId] = useState('');
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<IssuedKey | null>(null);

  const isGateway = !target?.scope || target.source === 'gateway';
  const memberIds = useMemo(
    () => (target?.scope === 'department' && target.scopeId ? departmentMemberIds(quotaDirectory, target.scopeId) : []),
    [quotaDirectory, target]
  );
  const users = useMemo(
    () => {
      if (!target) return [];
      if (isGateway || !target.scope || !target.scopeId) return quotaDirectory.users;
      return coveredUsers(quotaDirectory.users, memberIds, target.scope, target.scopeId);
    },
    [isGateway, memberIds, quotaDirectory.users, target]
  );

  useEffect(() => {
    if (!target) {
      setUserId('');
      setIssued(null);
      setBusy(false);
      return;
    }
    setIssued(null);
    setBusy(false);
    setUserId(!isGateway && target.scope === 'user' && target.scopeId ? target.scopeId : users[0]?.id || '');
  }, [isGateway, target?.id, target?.scope, target?.scopeId, users]);

  if (!target) return null;

  const selected =
    users.find((user) => user.id === userId) ||
    (!isGateway && target.scope === 'user'
      ? {
          id: target.scopeId || '',
          name: target.scopeLabel || '',
          email: '',
          roleId: '',
          departmentId: '',
        }
      : null);
  const baseUrl = llmInferenceBaseUrl(target.routeKey);
  const kind = (target.llmType || '').toLowerCase().includes('embed') ? 'embedding' : 'chat';
  const sample = issued
    ? llmInferenceCurlSample({
        baseUrl: issued.baseUrl || baseUrl,
        token: issued.token,
        model: issued.model || target.publicAlias,
        kind,
      })
    : '';

  const copy = async (text: string, label: string) => {
    if (!text) {
      onToast('error', `${label} is empty`);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      onToast('success', `${label} copied`);
      logGovernedMcp('app', 'inference_key_copied', { label, quotaId: target.id });
    } catch {
      onToast('error', 'Could not copy to clipboard');
      logGovernedMcp('warning', 'inference_key_copy_failed', { label, quotaId: target.id });
    }
  };

  const issue = async () => {
    if (!target.routeKey) {
      onToast('error', 'This gateway is missing a call URL. Save the gateway and try again.');
      return;
    }
    if (!selected) {
      onToast('error', isGateway ? 'Select a user for this inference key.' : 'Select a user covered by this quota assignment.');
      return;
    }
    setBusy(true);
    logGovernedMcp('app', 'inference_key_issue_submit', {
      quotaId: target.id,
      gatewayId: target.id,
      scope: target.scope || 'user',
      source: isGateway ? 'gateway' : 'quota',
      userId: selected.id,
    });
    try {
      const next = isGateway
        ? await issueGatewayInferenceKey({
            gatewayId: target.id,
            userId: selected.id,
            email: selected.email,
            name: selected.name,
            roleId: selected.roleId,
            departmentId: selected.departmentId,
          })
        : await issueInferenceKey({
            quotaId: target.id,
            userId: selected.id,
            email: selected.email,
            name: selected.name,
            roleId: selected.roleId,
            departmentId: selected.departmentId,
          });
      setIssued(next);
      onIssued?.({ last4: next.last4 });
      onToast('success', 'Inference key issued. Copy it now — it is shown once.');
    } catch (error) {
      onToast('error', error instanceof Error ? error.message : 'Could not issue an inference key.');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[3px]" aria-label="Close" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="inference-key-title"
        className={`relative w-full max-w-lg rounded-2xl border shadow-2xl overflow-hidden ${
          isLight ? 'border-neutral-200 bg-white' : 'border-neutral-800 bg-neutral-950'
        }`}
      >
        <div className="px-5 pt-5 pb-4 flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'
              }`}
            >
              <KeyRound size={18} />
            </div>
            <div className="min-w-0">
              <h3 id="inference-key-title" className={`text-base font-semibold tracking-tight ${isLight ? 'text-neutral-900' : 'text-white'}`}>
                {issued ? 'Copy this inference key' : target.hasInferenceKey ? 'Reissue inference key' : 'Issue inference key'}
              </h3>
              <p className={`text-sm mt-1.5 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                {issued
                  ? 'The plaintext key is shown once. Send it as Authorization: Bearer or as x-api-key — the same key works for both.'
                  : isGateway
                    ? 'Issue a key for this LLM gateway. Quota is assigned separately.'
                    : 'Issue a key for this LLM gateway assignment. Tokens are metered for the selected user.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1.5 rounded-lg shrink-0 ${isLight ? 'hover:bg-neutral-100 text-neutral-400' : 'hover:bg-neutral-800 text-neutral-500'}`}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 pb-4 space-y-4">
          <div className={`rounded-xl border px-3.5 py-3 text-sm ${isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-900/70'}`}>
            <p className={`font-medium truncate ${isLight ? 'text-neutral-900' : 'text-white'}`}>{target.name}</p>
            <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
              {[target.scopeLabel, target.publicAlias || 'No public alias'].filter(Boolean).join(' · ')}
              {target.hasInferenceKey && target.inferenceKeyLast4 ? ` · last four ${target.inferenceKeyLast4}` : ''}
            </p>
          </div>
          <div>
            <p className={labelClass(isLight)}>Call URL</p>
            <div className="flex items-center gap-2">
              <input className={`${fieldClass(isLight)} font-mono text-xs`} readOnly value={issued?.baseUrl || baseUrl} />
              <button type="button" className={ghostBtn(isLight)} onClick={() => void copy(issued?.baseUrl || baseUrl, 'Call URL')}>
                <Copy size={14} />
              </button>
            </div>
          </div>
          {issued ? (
            <>
              <div>
                <p className={labelClass(isLight)}>API key</p>
                <div className="flex items-center gap-2">
                  <input className={`${fieldClass(isLight)} font-mono text-xs`} readOnly value={issued.token} />
                  <button type="button" className={ghostBtn(isLight)} onClick={() => void copy(issued.token, 'API key')}>
                    <Copy size={14} />
                  </button>
                </div>
                <p className={helperClass(isLight)}>Expires {new Date(issued.expiresAt).toLocaleString()} · last four {issued.last4}</p>
              </div>
              <div>
                <p className={labelClass(isLight)}>Sample request</p>
                <pre
                  className={`mt-1 max-h-40 overflow-auto rounded-xl border p-3 text-[11px] leading-relaxed whitespace-pre-wrap ${
                    isLight ? 'border-neutral-200 bg-neutral-50 text-neutral-700' : 'border-neutral-800 bg-neutral-900 text-neutral-300'
                  }`}
                >
                  {sample}
                </pre>
                <button type="button" className={`${ghostBtn(isLight)} mt-2`} onClick={() => void copy(sample, 'Sample')}>
                  <Copy size={14} /> Copy sample
                </button>
              </div>
            </>
          ) : !isGateway && target.scope === 'user' ? (
            <p className={`text-sm ${isLight ? 'text-neutral-600' : 'text-neutral-300'}`}>
              Issues for {selected?.name || target.scopeLabel}.
            </p>
          ) : (
            <SearchableSelect
              label="Covered user"
              value={userId}
              options={users.map((user) => ({ id: user.id, label: user.name, hint: user.email }))}
              onChange={setUserId}
              isLight={isLight}
              placeholder={isGateway ? 'Select a user' : 'Select a user in this assignment'}
            />
          )}
        </div>

        <div
          className={`px-5 py-3.5 flex justify-end gap-2 border-t ${
            isLight ? 'border-neutral-100 bg-neutral-50/80' : 'border-neutral-800 bg-neutral-900/80'
          }`}
        >
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            {issued ? 'Done' : 'Cancel'}
          </button>
          {issued ? null : (
            <button type="button" className={primaryBtn()} disabled={busy || !selected} onClick={() => void issue()}>
              {busy ? 'Issuing…' : target.hasInferenceKey ? 'Reissue key' : 'Issue key'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
